import { createContext, useContext, useEffect, useState, useCallback } from "react";

const STORAGE_KEY = "fk-cookie-consent-v1";
const CONSENT_EVENT = "fk-consent-changed";

const DEFAULT_CONSENT = {
  necessary: true, // always true, cannot be turned off
  statistics: false,
  marketing: false,
};

const CookieConsentContext = createContext(null);

function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return {
      necessary: true,
      statistics: !!parsed.statistics,
      marketing: !!parsed.marketing,
      timestamp: parsed.timestamp || null,
    };
  } catch {
    return null;
  }
}

function writeStored(consent) {
  const payload = {
    ...consent,
    necessary: true,
    timestamp: new Date().toISOString(),
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* ignore quota / private-mode errors */
  }
  return payload;
}

export const CookieConsentProvider = ({ children }) => {
  const [consent, setConsent] = useState(DEFAULT_CONSENT);
  const [hasChosen, setHasChosen] = useState(false);
  const [bannerOpen, setBannerOpen] = useState(false);

  useEffect(() => {
    const stored = readStored();
    if (stored) {
      setConsent(stored);
      setHasChosen(true);
      setBannerOpen(false);
    } else {
      setBannerOpen(true);
    }
  }, []);

  const commit = useCallback((next) => {
    const payload = writeStored(next);
    setConsent(payload);
    setHasChosen(true);
    setBannerOpen(false);
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: payload }));
  }, []);

  const acceptAll = useCallback(() => {
    commit({ necessary: true, statistics: true, marketing: true });
  }, [commit]);

  const rejectAll = useCallback(() => {
    commit({ necessary: true, statistics: false, marketing: false });
  }, [commit]);

  const saveCustom = useCallback(
    (partial) => commit({ necessary: true, ...consent, ...partial }),
    [commit, consent]
  );

  const openBanner = useCallback(() => setBannerOpen(true), []);

  return (
    <CookieConsentContext.Provider
      value={{
        consent,
        hasChosen,
        bannerOpen,
        acceptAll,
        rejectAll,
        saveCustom,
        openBanner,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
};

export const useCookieConsent = () => {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) throw new Error("useCookieConsent must be inside CookieConsentProvider");
  return ctx;
};

export { CONSENT_EVENT, STORAGE_KEY };
